/**
 * MosaicHero.js — v6.32.162
 *
 * Three-Moment Hero strip for the Teacher Dashboard. Three cards:
 *   SCREEN → MONITOR → BENCHMARK
 *
 * Independently developed for NYC ENL/ELL programs. Not derived from any
 * third-party assessment provider's copyrighted materials.
 */
import React from 'react';
import { ClipboardList, Activity, Target, ChevronRight } from 'lucide-react';

const MOMENTS = [
  {
    key: 'screen',
    eyebrow: 'Moment 1',
    title: 'Screen',
    desc: 'New arrival? Run a 10-minute intake to place every multilingual learner on the MOSAIC ladder.',
    accent: 'from-rose-50 to-orange-50',
    ring: 'ring-rose-200',
    icon: ClipboardList,
    iconBg: 'bg-rose-500',
    cta: 'Open roster intake',
    targetTab: 'classes',
  },
  {
    key: 'monitor',
    eyebrow: 'Moment 2',
    title: 'Monitor',
    desc: 'Watch growth across Listening, Speaking, Reading, and Writing — with original Can-Do action statements.',
    accent: 'from-amber-50 to-emerald-50',
    ring: 'ring-amber-200',
    icon: Activity,
    iconBg: 'bg-amber-500',
    cta: 'View growth',
    targetTab: 'growth',
  },
  {
    key: 'benchmark',
    eyebrow: 'Moment 3',
    title: 'Benchmark',
    desc: 'Run a MOSAIC benchmark exam, then publish a parent-friendly Action Panel for every student.',
    accent: 'from-emerald-50 to-blue-50',
    ring: 'ring-blue-200',
    icon: Target,
    iconBg: 'bg-blue-600',
    cta: 'Assign benchmark',
    targetTab: 'assignments',
  },
];

const MosaicHero = ({ onJumpTo }) => {
  return (
    <section
      data-testid="mosaic-hero"
      className="mb-8 border-2 border-gray-900 bg-white"
    >
      <header className="flex items-baseline justify-between border-b-2 border-gray-900 px-5 py-3 bg-gray-900 text-white">
        <h2 className="text-base font-bold tracking-wide uppercase" style={{ fontFamily: 'Outfit, sans-serif' }}>
          The MOSAIC Three Moments
        </h2>
        <span className="text-xs text-gray-300 tracking-wider hidden md:inline">
          Screen · Monitor · Benchmark
        </span>
      </header>
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-300">
        {MOMENTS.map((m, idx) => {
          const Icon = m.icon;
          return (
            <button
              key={m.key}
              type="button"
              onClick={() => onJumpTo && onJumpTo(m.targetTab)}
              data-testid={`mosaic-hero-${m.key}`}
              className={`group relative text-left p-6 transition-all bg-gradient-to-br ${m.accent} hover:ring-2 ${m.ring} hover:ring-inset focus:outline-none focus:ring-2 focus:ring-inset focus:${m.ring}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-10 h-10 ${m.iconBg} flex items-center justify-center text-white shadow`}>
                  <Icon className="w-5 h-5" />
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-widest text-gray-500">
                    {m.eyebrow}
                  </div>
                  <div className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {m.title}
                  </div>
                </div>
                <span className="ml-auto text-xs text-gray-400 font-mono">
                  {String(idx + 1).padStart(2, '0')}
                </span>
              </div>
              <p className="text-sm text-gray-700 leading-snug mb-4">{m.desc}</p>
              <div className="flex items-center text-sm font-semibold text-gray-900 group-hover:translate-x-1 transition-transform">
                <span>{m.cta}</span>
                <ChevronRight className="w-4 h-4 ml-1" />
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
};

export default MosaicHero;
